import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useRef } from 'react';
import { jwtDecode } from 'jwt-decode';

import { authApi } from '../../../core/api/auth.api';
import { useAuthProviderV2, useRoutesProvider } from '../../../providers/provider-hooks';
import type { UserInfo } from '../../../providers/auth-provider.types';
import { resolvePostAuthTarget } from '../utils/resolve-post-auth-target';
import { pasiInscriereActive } from '../utils/pasi-inscriere';
import { exchangeErrorMessage, extractGoogleCredentialEmail, shouldShowReenrollHint } from '../utils/google-sign-in';

/**
 * Schimba un ID token Google pe token-urile aplicatiei si navigheaza, la fel ca `useLoginWithForm`.
 * Eroarea NU trece prin toast — `GoogleSignInButton` o afiseaza inline, langa buton.
 */
export function useGoogleExchange() {
  const { updateToken, updateRefreshToken } = useAuthProviderV2();
  const navigate = useNavigate();
  const { getUserHomePage, PATHS, PAGES, ROLES } = useRoutesProvider();
  // Credentialul Google e nevoie doar la un 401, ca sa incercam sa extragem emailul pentru
  // reinscriere - mutation.mutate() nu-l pastreaza dupa apel.
  const lastCredentialRef = useRef<string | null>(null);

  const mutation = useMutation({
    mutationFn: (credential: string) => authApi.exchange({ provider: 'google', token: credential }),
    onSuccess: (data) => {
      if (!data.token || !data.refreshToken) {
        throw new Error('Authentication response missing tokens');
      }

      const token = data.token;

      updateRefreshToken(data.refreshToken);
      updateToken(token);

      try {
        const decoded = jwtDecode<UserInfo>(token);
        const target =
          resolvePostAuthTarget(decoded, null, {
            PATHS,
            PAGES,
            ROLES,
            getUserHomePage,
            pasiInscriere: pasiInscriereActive(),
          }) ??
          getUserHomePage(decoded.role);
        navigate(target, { replace: true });
      } catch (error) {
        console.error('Failed to decode JWT token or navigate:', error);
        navigate(getUserHomePage(), { replace: true });
      }
    },
    onError: (error) => {
      console.error('Google exchange error:', error);
    },
  });

  const handleCredential = (credential: string) => {
    lastCredentialRef.current = credential;
    mutation.mutate(credential);
  };

  const navigateToReenroll = () => {
    // Google nu ofera o parola veche, spre deosebire de useLoginWithForm - state-ul poarta
    // DOAR email, sau nimic daca decodarea credentialului esueaza.
    const email = lastCredentialRef.current
      ? extractGoogleCredentialEmail(lastCredentialRef.current)
      : undefined;
    navigate(PATHS[PAGES.PUPIL_REENROLL], email ? { state: { email } } : undefined);
  };

  return {
    handleCredential,
    isPending: mutation.isPending,
    errorMessage: mutation.isError ? exchangeErrorMessage(mutation.error) : null,
    showReenrollHint: mutation.isError && shouldShowReenrollHint(mutation.error),
    navigateToReenroll,
  };
}
